import { c as _c } from "react/compiler-runtime";
import { Button, Status } from '@systemeio/components';
import { Flag01Icon, LinkExternal01Icon, PlayIcon, StopIcon } from '@systemeio/icons';
import { finishSplitTest, removeSplitTestVariant, startSplitTest } from 'modules/funnels/funnel/api/funnel-api';
import { FunnelTabCard } from 'modules/funnels/funnel/components/funnel-tab-card';
import ChooseTestVariantModal from 'modules/funnels/funnel/components/split-tests-tab/choose-test-variant-modal';
import { SplitTestCardsWrapper, SplitTestSeparator } from 'modules/funnels/funnel/components/split-tests-tab/split-test-compound';
import SplitTestItem from 'modules/funnels/funnel/components/split-tests-tab/split-test-item';
import { SplitTestPeriod } from 'modules/funnels/funnel/components/split-tests-tab/split-test-period';
import TabTable from 'modules/funnels/funnel/components/tab-table';
import { SplitTestComponentTypeEnum } from 'modules/funnels/funnel/enums/split-tests-component-type-enum';
import { useFunnelStepTests } from 'modules/funnels/funnel/hooks/use-funnel-step-tests';
import { useFunnelStepTestsHistory } from 'modules/funnels/funnel/hooks/use-funnel-step-tests-history';
import { useFunnelStepsList } from 'modules/funnels/funnel/hooks/use-funnel-steps-list';
import { useSplitTestTableColumns } from 'modules/funnels/funnel/hooks/use-split-test-table-columns';
import { useUpdateFunnelStepQuery } from 'modules/funnels/funnel/hooks/use-update-funnel-step-query';
import { FunnelStepId, FunnelStepInterface } from 'modules/funnels/funnel/types/funnel-step-interface';
import { NotStartedSplitTestInterface, SplitTestInterface, SplitTestType } from 'modules/funnels/funnel/types/funnel-tests-interface';
import { getRunningTestTableData, isSplitTestIsEmptyArray, isSplitTestIsNotStarted, isSplitTestIsStarted } from 'modules/funnels/funnel/utils/split-tests-utils';
import { FunnelId } from 'modules/funnels/types/funnel-interface';
import React, { useState, type ReactNode } from 'react';
import { toast } from 'shared/components';
import Confirmation from 'shared/components/confirmation-modal';
import { ViewportSwitch } from 'shared/components/viewport-switch';
import { CARD_FRAME_CLASSNAME } from 'shared/constants/card-class-names';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { twJoin } from 'tailwind-merge';
interface SplitTestActionItemProps {
  data: SplitTestType;
  funnelId: FunnelId;
  funnelStep: FunnelStepInterface;
}
function SplitTestActions({
  data,
  funnelId,
  funnelStep
}: SplitTestActionItemProps) {
  const {
    t
  } = useLocoTranslation();
  const [isDeleteVariantOpen, setIsDeleVariantOpen] = useState(false);
  const [isEndSplitTestOpen, setIsEndSplitTestOpen] = useState(false);
  const {
    mutateSplitTest
  } = useFunnelStepTests(funnelStep.id);
  const {
    mutateFunnelStepsList
  } = useFunnelStepsList(funnelId);
  const {
    mutateHistoryData
  } = useFunnelStepTestsHistory(funnelStep.id);
  const columns = useSplitTestTableColumns();
  const {
    updateFunnelStepQuery
  } = useUpdateFunnelStepQuery();
  const [isFetching, setIsFetching] = useState(false);
  const handleDeleteVariant = async () => {
    const castedData = data as NotStartedSplitTestInterface;
    setIsFetching(true);
    await removeSplitTestVariant(funnelStep.id);
    await mutateFunnelStepsList(data_0 => {
      if (data_0) {
        return data_0.map(funnelStep_0 => {
          if (funnelStep_0.id === castedData.funnelStepA.id || funnelStep_0.id === castedData.funnelStepB.id) {
            return {
              ...funnelStep_0,
              inRunningSplitTest: false,
              currentSplitTest: null
            };
          }
          if (funnelStep_0.id === castedData.goalFunnelStep.id) {
            return {
              ...funnelStep_0,
              goalFunnelStepOfAnySplitTest: false,
              currentSplitTest: null
            };
          }
          return funnelStep_0;
        });
      }
    }, false);
    await mutateSplitTest([], false);
    setIsFetching(false);
  };
  const handleEndSplitTest = async () => {
    setIsFetching(true);
    const castedData_0 = data as SplitTestInterface;
    const response = await finishSplitTest(funnelStep.id);
    await mutateHistoryData(response.data, false);
    await mutateFunnelStepsList(data_1 => {
      if (data_1) {
        return data_1.map(funnelStep_1 => {
          if (funnelStep_1.id === castedData_0.splitTest.funnelStepA.id || funnelStep_1.id === castedData_0.splitTest.funnelStepB.id) {
            return {
              ...funnelStep_1,
              inRunningSplitTest: false,
              currentSplitTest: null
            };
          }
          if (funnelStep_1.id === castedData_0.splitTest.goalFunnelStep.id) {
            return {
              ...funnelStep_1,
              goalFunnelStepOfAnySplitTest: false,
              currentSplitTest: null
            };
          }
          return funnelStep_1;
        });
      }
    }, false);
    await mutateSplitTest([], false);
    setIsFetching(false);
  };
  const handleStartSplitTest = async () => {
    try {
      setIsFetching(true);
      const response_0 = await startSplitTest(funnelStep.id);
      await mutateSplitTest(response_0.data, false);
      await mutateFunnelStepsList(data_2 => {
        if (data_2) {
          return data_2.map(funnelStep_2 => {
            if (funnelStep_2.id === response_0.data.splitTest.funnelStepA.id || funnelStep_2.id === response_0.data.splitTest.funnelStepB.id) {
              return {
                ...funnelStep_2,
                inRunningSplitTest: true,
                currentSplitTest: {
                  id: response_0.data.splitTest.id,
                  funnelStepA: {
                    id: response_0.data.splitTest.funnelStepA.id,
                    page: response_0.data.splitTest.funnelStepA.page
                  },
                  funnelStepB: {
                    id: response_0.data.splitTest.funnelStepB.id,
                    page: response_0.data.splitTest.funnelStepB.page
                  }
                }
              };
            }
            return funnelStep_2;
          });
        }
      }, false);
    } catch (error) {
      toast.error(t('global.error'));
    } finally {
      setIsFetching(false);
    }
  };
  const handleChangeFunnelStepConfigurationTab = (id: FunnelStepId) => {
    updateFunnelStepQuery(id);
  };
  if (isSplitTestIsEmptyArray(data)) {
    return <SplitTestCardsWrapper>
        <SplitTestItem isCurrent={true} type={SplitTestComponentTypeEnum.original} funnelStep={funnelStep} />
        <SplitTestSeparator />
        <SplitTestItem type={SplitTestComponentTypeEnum.variant} title={t('dashboard.funnel.split_tests.start_test')} description={t('dashboard.funnel.split_tests.create_test_description')} action={<ChooseTestVariantModal funnelId={funnelId} funnelStep={funnelStep} />} />
      </SplitTestCardsWrapper>;
  }
  if (isSplitTestIsNotStarted(data)) {
    const castedData_1 = data as NotStartedSplitTestInterface;
    return <>
        <div className="tablet:gap-5 flex flex-col gap-4">
          <GoalPage funnelStep={castedData_1.goalFunnelStep} onEdit={handleChangeFunnelStepConfigurationTab} />
          <SplitTestCardsWrapper>
            <SplitTestItem isCurrent={funnelStep.id === castedData_1.funnelStepA.id} type={SplitTestComponentTypeEnum.original} funnelStep={castedData_1.funnelStepA} />
            <SplitTestSeparator />
            <SplitTestItem isCurrent={funnelStep.id === castedData_1.funnelStepB.id} type={SplitTestComponentTypeEnum.variant} funnelStep={castedData_1.funnelStepB} />
          </SplitTestCardsWrapper>
          <div className="tablet:flex-row tablet:items-center flex flex-col items-stretch justify-center gap-3">
            <ViewportSwitch at="desktop" below={null} from={<Status size={'lg'} color="inactive">
                  {t('dashboard.funnel.split_tests.not_running')}
                </Status>} />

            <ViewportSwitch at="tablet" below={<MobileActionBar>
                  <Button className={'w-full'} variant="secondary" onClick={() => setIsDeleVariantOpen(true)}>
                    {t('global.cancel')}
                  </Button>
                  <Button variant={'tertiary-color'} className={'w-full'} onClick={handleStartSplitTest} isLoading={isFetching}>
                    <PlayIcon />
                    {t('dashboard.funnel.split_tests.start_test')}
                  </Button>
                </MobileActionBar>} from={<>
                  <div aria-hidden="true" className="border-border-100 self-stretch border-r" />
                  <Button onClick={handleStartSplitTest} isLoading={isFetching}>
                    <PlayIcon />
                    {t('dashboard.funnel.split_tests.start_test')}
                  </Button>
                  <Button variant="secondary" onClick={() => setIsDeleVariantOpen(true)}>
                    {t('global.cancel')}
                  </Button>
                </>} />
          </div>
        </div>
        <Confirmation status="error" confirmationText={t('dashboard.funnel.split_tests.remove_variant_confirmation')} opened={isDeleteVariantOpen} onClose={() => setIsDeleVariantOpen(false)} onConfirm={handleDeleteVariant} toastCaption={t('dashboard.funnel.split_tests.deleted_variant_toast')} />
      </>;
  }
  if (isSplitTestIsStarted(data)) {
    const castedData_2 = data as SplitTestInterface;
    const tableData = getRunningTestTableData(castedData_2);
    return <>
        <div className="tablet:gap-5 flex flex-col gap-4">
          <GoalPage funnelStep={castedData_2.splitTest.goalFunnelStep} onEdit={handleChangeFunnelStepConfigurationTab} />
          <SplitTestCardsWrapper>
            <SplitTestItem isCurrent={funnelStep.id === castedData_2.splitTest.funnelStepA.id} type={SplitTestComponentTypeEnum.original} funnelStep={castedData_2.splitTest.funnelStepA} />
            <SplitTestSeparator />
            <SplitTestItem isCurrent={funnelStep.id === castedData_2.splitTest.funnelStepB.id} type={SplitTestComponentTypeEnum.variant} funnelStep={castedData_2.splitTest.funnelStepB} />
          </SplitTestCardsWrapper>
          <ViewportSwitch at="tablet" below={<MobileActionBar>
                <Button className="w-full" onClick={() => setIsEndSplitTestOpen(true)}>
                  <StopIcon />
                  {t('dashboard.funnel.split_tests.end_test')}
                </Button>
              </MobileActionBar>} from={<div className="flex justify-center">
                <Button onClick={() => setIsEndSplitTestOpen(true)}>
                  <StopIcon />
                  {t('dashboard.funnel.split_tests.end_test')}
                </Button>
              </div>} />
        </div>
        <Confirmation status="error" confirmationText={t('dashboard.funnel.split_tests.finish_test_confirmation')} opened={isEndSplitTestOpen} onClose={() => setIsEndSplitTestOpen(false)} onConfirm={handleEndSplitTest} toastCaption={t('dashboard.funnel.split_tests.finish_test_toast')} confirmButtonCaption={t('dashboard.funnel.split_tests.end_test')} />
        <div className="flex flex-col gap-2">
          <SplitTestPeriod splitTest={castedData_2.splitTest} />
          <TabTable columns={columns} data={tableData} />
        </div>
      </>;
  }
  return <></>;
}
function MobileActionBar(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="border-border-100 bg-bg-primary fixed inset-x-0 bottom-0 z-40 flex gap-3 border-t p-4">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function GoalPage(t0) {
  const $ = _c(45);
  const {
    funnelStep,
    onEdit
  } = t0;
  const {
    t
  } = useLocoTranslation();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = twJoin(CARD_FRAME_CLASSNAME, "flex flex-col");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== t) {
    t2 = t("dashboard.funnel.split_tests.goal_funnel_step");
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <span className="text-base">{t2}</span>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t("dashboard.funnel.split_tests.goal.description");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <span className="text-text-tertiary text-sm">{t4}</span>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t5) {
    t6 = <div className="border-table-cell-border flex flex-col border-b px-4 py-3">{t3}{t5}</div>;
    $[9] = t3;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== funnelStep.id || $[13] !== onEdit) {
    t7 = () => onEdit(funnelStep.id);
    $[12] = funnelStep.id;
    $[13] = onEdit;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== funnelStep.name || $[16] !== t7) {
    t8 = <div className="flex self-end px-4 py-3"><Button className="inline text-start whitespace-normal" variant="link" size="sm" onClick={t7}>{funnelStep.name}</Button></div>;
    $[15] = funnelStep.name;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t6 || $[19] !== t8) {
    t9 = <div className={t1}>{t6}{t8}</div>;
    $[18] = t6;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Flag01Icon className="text-table-cell-icon size-5 shrink-0" />;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t) {
    t11 = t("dashboard.funnel.split_tests.goal_funnel_step");
    $[22] = t;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11) {
    t12 = <span className="text-base">{t11}</span>;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t) {
    t13 = t("dashboard.funnel.split_tests.goal.description");
    $[26] = t;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t13) {
    t14 = <span className="text-text-tertiary text-sm">{t13}</span>;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t12 || $[31] !== t14) {
    t15 = <div className="flex min-w-0 items-center gap-3">{t10}<div className="flex min-w-0 flex-col">{t12}{t14}</div></div>;
    $[30] = t12;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== funnelStep.id || $[34] !== onEdit) {
    t16 = () => onEdit(funnelStep.id);
    $[33] = funnelStep.id;
    $[34] = onEdit;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== funnelStep.name || $[37] !== t16) {
    t17 = <Button variant="link" size="sm" onClick={t16}>{funnelStep.name}</Button>;
    $[36] = funnelStep.name;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t15 || $[40] !== t17) {
    t18 = <FunnelTabCard className="tablet:flex-row flex flex-col items-center justify-between gap-20">{t15}{t17}</FunnelTabCard>;
    $[39] = t15;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t18 || $[43] !== t9) {
    t19 = <ViewportSwitch at="tablet" below={t9} from={t18} />;
    $[42] = t18;
    $[43] = t9;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  return t19;
}
export default SplitTestActions;